import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, Plus, Edit2, Trash2, X,
  CalendarDays, List, Loader2, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import api from '../../api';

const EVENT_TYPES = [
  { value: 'holiday', label: 'Holiday', color: '#ef4444', bg: 'bg-red-100', text: 'text-red-700' },
  { value: 'exam', label: 'Exam', color: '#f59e0b', bg: 'bg-amber-100', text: 'text-amber-700' },
  { value: 'activity', label: 'Activity', color: '#10b981', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  { value: 'meeting', label: 'Meeting', color: '#3b82f6', bg: 'bg-blue-100', text: 'text-blue-700' },
  { value: 'announcement', label: 'Announcement', color: '#8b5cf6', bg: 'bg-violet-100', text: 'text-violet-700' },
  { value: 'other', label: 'Other', color: '#6b7280', bg: 'bg-slate-100', text: 'text-slate-600' },
];

const typeStyle = (type) => EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[5];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function EventsPage({ readOnly = false }) {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('calendar'); // calendar | list
  const [editEvent, setEditEvent] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createDate, setCreateDate] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/events?month=${monthKey}`);
      setEvents(res.data || []);
    } catch { toast.error('Failed to load events'); }
    finally { setLoading(false); }
  }, [monthKey]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  // Build calendar grid
  const firstDay = currentDate.getDay();
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const eventsForDay = (d) => {
    const dateStr = `${monthKey}-${String(d).padStart(2, '0')}`;
    return events.filter(e => e.date <= dateStr && (e.end_date || e.date) >= dateStr);
  };

  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
  const selectedStr = selectedDay ? `${monthKey}-${String(selectedDay).padStart(2,'0')}` : null;

  const upcomingEvents = [...events].filter(e => e.date >= todayStr).sort((a,b)=>a.date.localeCompare(b.date)).slice(0, 8);

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-5" data-testid="events-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">School Calendar</h1>
          <p className="text-sm text-slate-500 mt-0.5">Events, holidays, exams, and activities</p>
        </div>
        <div className="flex gap-2 items-center">
          <button onClick={() => setView(v => v === 'calendar' ? 'list' : 'calendar')}
            className="flex items-center gap-1.5 border border-slate-200 text-slate-600 text-sm font-semibold px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
            {view === 'calendar' ? <List className="w-4 h-4" /> : <CalendarDays className="w-4 h-4" />}
            {view === 'calendar' ? 'List' : 'Calendar'}
          </button>
          {!readOnly && (
            <button onClick={() => { setCreateDate(todayStr); setShowCreate(true); }}
              className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors"
              data-testid="add-event-btn">
              <Plus className="w-4 h-4" /> Add Event
            </button>
          )}
        </div>
      </div>

      {view === 'calendar' ? (
        <div className="grid lg:grid-cols-[1fr_300px] gap-5">
          {/* Calendar */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            {/* Month nav */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                <ChevronLeft className="w-5 h-5 text-slate-500" />
              </button>
              <h2 className="font-outfit text-lg font-bold text-slate-900">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                <ChevronRight className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 border-b border-slate-100">
              {DAYS.map(d => (
                <div key={d} className="py-2 text-center text-xs font-bold text-slate-400 uppercase tracking-wide">{d}</div>
              ))}
            </div>

            {/* Calendar cells */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-7 h-7 text-indigo-500 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-7">
                {cells.map((d, i) => {
                  const dayEvents = d ? eventsForDay(d) : [];
                  const dateStr = d ? `${monthKey}-${String(d).padStart(2,'0')}` : null;
                  const isToday = dateStr === todayStr;
                  const isSelected = dateStr === selectedStr;
                  return (
                    <div key={i}
                      onClick={() => d && setSelectedDay(d === selectedDay ? null : d)}
                      className={`min-h-[80px] p-1.5 border-b border-r border-slate-50 cursor-pointer transition-colors
                        ${d ? 'hover:bg-indigo-50/40' : ''}
                        ${isSelected ? 'bg-indigo-50' : ''}
                      `}>
                      {d && (
                        <>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold mb-1 mx-auto
                            ${isToday ? 'bg-indigo-600 text-white' : 'text-slate-700'}
                          `}>
                            {d}
                          </div>
                          <div className="space-y-0.5">
                            {dayEvents.slice(0, 2).map(e => (
                              <div key={e.id}
                                style={{ backgroundColor: e.color + '22', color: e.color }}
                                className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md truncate leading-tight">
                                {e.title}
                              </div>
                            ))}
                            {dayEvents.length > 2 && (
                              <div className="text-[10px] text-slate-400 pl-1">+{dayEvents.length - 2}</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Selected day events */}
            {selectedDay && (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-800 text-sm">
                    {MONTHS[currentDate.getMonth()]} {selectedDay}
                  </h3>
                  <button onClick={() => setSelectedDay(null)} className="text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-3 space-y-2">
                  {eventsForDay(selectedDay).length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-slate-400">No events</p>
                      {!readOnly && (
                        <button onClick={() => { setCreateDate(`${monthKey}-${String(selectedDay).padStart(2,'0')}`); setShowCreate(true); }}
                          className="text-xs text-indigo-500 hover:text-indigo-700 mt-1 font-semibold">+ Add event</button>
                      )}
                    </div>
                  ) : eventsForDay(selectedDay).map(e => (
                    <EventCard key={e.id} event={e} readOnly={readOnly}
                      onEdit={() => setEditEvent(e)}
                      onDelete={() => setDeleteId(e.id)} />
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming events */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100">
                <h3 className="font-semibold text-slate-800 text-sm">Upcoming Events</h3>
              </div>
              <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
                {upcomingEvents.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No upcoming events</p>
                ) : upcomingEvents.map(e => (
                  <EventCard key={e.id} event={e} readOnly={readOnly}
                    onEdit={() => setEditEvent(e)}
                    onDelete={() => setDeleteId(e.id)} />
                ))}
              </div>
            </div>

            {/* Legend */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Event Types</h3>
              <div className="space-y-1.5">
                {EVENT_TYPES.map(t => (
                  <div key={t.value} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: t.color }} />
                    <span className="text-xs text-slate-600 font-medium capitalize">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* List view */
        <ListView events={events} loading={loading} readOnly={readOnly}
          onEdit={setEditEvent} onDelete={setDeleteId} monthKey={monthKey}
          onPrev={prevMonth} onNext={nextMonth}
          month={`${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`} />
      )}

      {/* Create/Edit Modal */}
      {(showCreate || editEvent) && (
        <EventModal
          event={editEvent}
          defaultDate={editEvent?.date || createDate}
          onClose={() => { setShowCreate(false); setEditEvent(null); setCreateDate(''); }}
          onSuccess={() => { setShowCreate(false); setEditEvent(null); setCreateDate(''); load(); }} />
      )}

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove the event from the calendar.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={async () => {
              try {
                await api.delete(`/events/${deleteId}`);
                toast.success('Event deleted');
                setDeleteId(null);
                load();
              } catch { toast.error('Failed to delete'); }
            }} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ─── Event Card ─────────────────────────────────────────────────── */
function EventCard({ event, readOnly, onEdit, onDelete }) {
  const s = typeStyle(event.type);
  return (
    <div className={`flex items-start gap-2.5 p-2.5 rounded-xl border ${s.bg} border-opacity-50`}
      style={{ borderColor: event.color + '44' }}>
      <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: event.color }} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 leading-tight truncate">{event.title}</p>
        <p className="text-[10px] text-slate-500 mt-0.5">{event.date}{event.end_date && event.end_date !== event.date ? ` → ${event.end_date}` : ''}</p>
        {event.description && <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{event.description}</p>}
      </div>
      {!readOnly && (
        <div className="flex gap-1 flex-shrink-0">
          <button onClick={onEdit} className="p-1 rounded hover:bg-white/60 text-slate-400 hover:text-indigo-600 transition-colors">
            <Edit2 className="w-3 h-3" />
          </button>
          <button onClick={onDelete} className="p-1 rounded hover:bg-white/60 text-slate-400 hover:text-red-500 transition-colors">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── List View ──────────────────────────────────────────────────── */
function ListView({ events, loading, readOnly, onEdit, onDelete, month, onPrev, onNext, monthKey }) {
  const grouped = {};
  events.forEach(e => {
    if (!grouped[e.date]) grouped[e.date] = [];
    grouped[e.date].push(e);
  });
  const sortedDates = Object.keys(grouped).sort();

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
        <button onClick={onPrev} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
          <ChevronLeft className="w-5 h-5 text-slate-500" />
        </button>
        <h2 className="font-outfit text-lg font-bold text-slate-900">{month}</h2>
        <button onClick={onNext} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
          <ChevronRight className="w-5 h-5 text-slate-500" />
        </button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-indigo-500 animate-spin" /></div>
      ) : sortedDates.length === 0 ? (
        <div className="text-center py-16">
          <Calendar className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-400">No events this month</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-50">
          {sortedDates.map(date => (
            <div key={date} className="px-6 py-4">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">
                {new Date(date + 'T00:00:00').toLocaleDateString('en-NG', { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
              <div className="space-y-2">
                {grouped[date].map(e => (
                  <EventCard key={e.id} event={e} readOnly={readOnly} onEdit={() => onEdit(e)} onDelete={() => onDelete(e.id)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Event Create/Edit Modal ───────────────────────────────────── */
function EventModal({ event, defaultDate, onClose, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: event?.title || '',
    description: event?.description || '',
    date: event?.date || defaultDate || '',
    end_date: event?.end_date || event?.date || defaultDate || '',
    type: event?.type || 'activity',
    all_day: event?.all_day !== false,
  });
  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.date) { toast.error('Title and date are required'); return; }
    if (form.end_date && form.end_date < form.date) { toast.error('End date cannot be before start date'); return; }
    setSaving(true);
    try {
      if (event?.id) {
        await api.put(`/events/${event.id}`, form);
        toast.success('Event updated');
      } else {
        await api.post('/events', form);
        toast.success('Event created');
      }
      onSuccess();
    } catch (err) { toast.error(err?.detail || 'Failed to save event'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-outfit text-lg">{event ? 'Edit Event' : 'Create Event'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Event Title *</Label>
            <Input value={form.title} onChange={e => upd('title', e.target.value)} placeholder="e.g. Mid-Term Examinations" className="h-9 text-sm" data-testid="event-title-input" />
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Event Type</Label>
            <div className="grid grid-cols-3 gap-1.5">
              {EVENT_TYPES.map(t => (
                <button key={t.value} type="button" onClick={() => upd('type', t.value)}
                  className={`px-2 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${form.type === t.value ? `${t.bg} ${t.text} border-current` : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Start Date *</Label>
              <Input type="date" value={form.date} onChange={e => { upd('date', e.target.value); if (!form.end_date || form.end_date < e.target.value) upd('end_date', e.target.value); }} className="h-9 text-sm" data-testid="event-date-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">End Date</Label>
              <Input type="date" value={form.end_date} min={form.date} onChange={e => upd('end_date', e.target.value)} className="h-9 text-sm" />
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Description (optional)</Label>
            <textarea value={form.description} onChange={e => upd('description', e.target.value)}
              placeholder="Add details about this event..."
              className="w-full h-20 text-sm border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-indigo-400 resize-none"
              data-testid="event-description-input" />
          </div>
        </div>
        <div className="flex gap-3 pt-1">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-event-btn">
            {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving...</> : event ? 'Update Event' : 'Create Event'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
